import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents, ProductSelectEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {object} ProductPersonalizationRefs
 * @property {HTMLButtonElement[]} [colorSwatch] - The bubble colour swatch buttons, when enabled.
 * @property {HTMLElement} [colorLabel] - Shows the selected colours or the max-colours warning.
 * @property {HTMLInputElement} [messageInput] - The bubble message input, when enabled.
 * @property {HTMLElement} [wordCount] - Shows the live word count for the message.
 * @property {HTMLButtonElement[]} [roseSwatch] - The rose colour swatch buttons, when enabled (single-select, or up to `data-max-rose-colors`).
 * @property {HTMLElement} [roseColorLabel] - Shows the selected rose colour(s) or the max-colours warning.
 * @property {HTMLInputElement} [colorPropertyInput] - Hidden `properties[Bubble Colour(s)]` input, kept in sync so the main line item carries the selection.
 * @property {HTMLInputElement} [messagePropertyInput] - Hidden `properties[Bubble Message]` input, same purpose.
 * @property {HTMLInputElement} [flowerThemePropertyInput] - Hidden `properties[Flower Colour Theme]` input, when enabled.
 * @property {HTMLInputElement} [roseColorPropertyInput] - Hidden `properties[Rose Colour]` input, when enabled.
 * @property {HTMLElement} [flowerDropdown] - The flower colour theme dropdown wrapper, when enabled.
 * @property {HTMLButtonElement} [flowerDropdownTrigger] - The flower dropdown's trigger button.
 * @property {HTMLElement} [flowerDropdownValue] - Shows the selected flower theme (or the placeholder).
 * @property {HTMLElement} [flowerDropdownList] - The flower dropdown's listbox.
 * @property {HTMLLIElement[]} [flowerDropdownOption] - The flower dropdown's option elements.
 * @property {HTMLElement} [cardWordCount] - Shows the live word count for the card message.
 * @property {HTMLTextAreaElement} [cardMessageInput] - The card message textarea.
 * @property {HTMLElement} [cardStatus] - Shows card add-on cart errors, if any.
 * @property {HTMLButtonElement} [cardRevealLink] - Cart-page reuse only (see snippets/cart-line-card-field.liquid): the "+ Add personalized card" link shown in place of the field until clicked.
 * @property {HTMLElement} [cardFieldWrapper] - Cart-page reuse only: wraps the card field, hidden until cardRevealLink is clicked (or the field already has a message at render time).
 * @property {HTMLElement} [cardConfirmation] - Cart-page reuse only: "Personalised card added. KSh X." line grouped with the field, shown instantly as #cardAdded flips.
 * @property {HTMLButtonElement} [ribbonToggle] - The "Add a personalised ribbon" toggle, when it's a paid add-on.
 * @property {HTMLElement} [ribbonDetails] - Wraps the ribbon message textarea + counter, when the ribbon is toggleable.
 * @property {HTMLTextAreaElement} [ribbonMessageInput] - The ribbon message textarea (paid toggle or always-on free field).
 * @property {HTMLElement} [ribbonWordCount] - Shows the live word count for the ribbon message.
 * @property {HTMLElement} [ribbonStatus] - Shows ribbon add-on cart errors, if any.
 * @property {HTMLElement} estimatedTotal - Shows the live "base price + active add-ons" total.
 * @property {HTMLInputElement} [numberPropertyInput] - Hidden `properties[Number]` input (card-only block, `show_number_picker`), holding e.g. "7" or "25"; disabled while empty.
 * @property {HTMLElement} [numberSingleGroup] - Wrapper for the single 0 to 9 dropdown.
 * @property {HTMLElement} [numberDoubleGroup] - Wrapper for the first/second digit dropdowns.
 * @property {HTMLSelectElement} [numberSingleSelect] - "Your number", 0 to 9.
 * @property {HTMLSelectElement} [numberFirstSelect] - "First digit", 1 to 9.
 * @property {HTMLSelectElement} [numberSecondSelect] - "Second digit", 0 to 9.
 * @property {HTMLInputElement} [preferredColoursInput] - `properties[Preferred colours]` text input (card-only block, `show_preferred_colours`).
 * @property {HTMLElement} [validationSummary] - The "before adding to cart, please choose…" checklist shown while a mandatory personalization field is unfilled (only rendered by blocks that have at least one `require_*` field).
 * @property {HTMLInputElement[]} [flowerMixInput] - The "Flower mix" radio inputs, when that selector is shown (standalone-bouquet pattern).
 * @property {HTMLElement} [flowerMatchingNote] - The flower-matching callout, when `show_flower_matching_note` is on — see #handleProductSelect for how its visibility is kept in sync with the selected variant.
 */

/**
 * Manages every personalization control that stays on a product page: bubble message +
 * colour, an optional flower colour theme dropdown, an optional rose colour picker, and the
 * "Personalized card" and "Personalised ribbon" add-ons — the two add-ons that stay native to
 * a specific product page (rather than moving to the cart) because they're physically tied to
 * one line item, e.g. two different bouquets in one order needing two different cards. Every
 * other add-on (wine, chocolates, teddy bear, foil balloons, ...) lives on the cart page now
 * (see assets/cart-addons.js) since those apply to the order as a whole, not to one product.
 *
 * REQUIRED vs OPTIONAL fields (see #updateReadyState / #missingRequiredFields): the core
 * "what am I actually making" choices — the bubble message, the bubble-colour swatches, the
 * flower-colour-theme dropdown (flower bubbles only), the "gift amount" / "denominations"
 * fields (Money Bubble only), and on the standalone-bouquet pattern the "Flower mix" selector,
 * flower/rose colour, and free personalization message (Flower Heart Box only), and the
 * card-only block's number picker and preferred colours field (Helium Numbers) — CAN each be
 * marked mandatory per block via its `require_*` setting (which renders as a `data-require-*`
 * attribute here). When any mandatory field is unfilled the product form's add-to-cart button
 * is disabled AND a visible checklist of what's still missing is shown just above it. The card /
 * ribbon / lilies / vase / matching-bouquet / "name on box" add-ons are ALWAYS optional and
 * never gate the form — leaving them blank is followed up on the order. A block that sets none
 * of the `require_*` flags (e.g. a plain card-only-personalization block) has no gate at all.
 *
 * The flower theme field is a custom button+listbox dropdown rather than a native <select>,
 * so its open option list can use the site's heading font (most browsers ignore custom fonts
 * on a native <select>'s open option list). Focus stays on the trigger button throughout, per
 * the ARIA "collapsible dropdown listbox" pattern: arrow keys move aria-activedescendant
 * across the options, Enter/Space selects, Escape closes.
 *
 * Card and ribbon are each their own standalone Shopify product (+KSh, per the "no app/
 * Function" decision), added to cart via AJAX rather than as a price mutation on the main
 * line. The card add-on activates automatically as soon as the customer types into its
 * message field (no separate checkbox) and deactivates when the field is cleared back to
 * empty; the ribbon add-on (where it's a paid toggle rather than an always-included free
 * field) keeps its own explicit toggle button.
 *
 * ROOT CAUSE FIX — cross-page carryover bug: every product's card/ribbon block points at the
 * *same* shared standalone variant (one "Personalized Card"/"Personalised ribbon" product
 * reused everywhere), and adding one is a real, persistent cart line. Before this fix, "is
 * this add-on active" was decided purely by "is that shared variant present in the cart at
 * all" — so a card added while viewing Product A would read as active on Product B's page too,
 * the next time B's page processed any cart update, inflating B's estimated total for
 * something the customer never touched there. The fix: every add adds a hidden
 * `_card_product_id`/`_ribbon_product_id` property naming the product that added it, and the
 * "is this active for me" check (#syncCardStateFromCartItems/#syncRibbonStateFromCartItems,
 * both via #findOwnLine) matches on that property too, not just the variant id. Because two
 * products' lines now carry different properties, Shopify keeps them as separate cart lines,
 * so remove/update calls target the specific line's `key` (learned from the add/change
 * response) rather than the bare variant id, which would be ambiguous once more than one line
 * of that variant can exist at once.
 *
 * ROOT CAUSE FIX #2 — same-product duplicate card line (order #1004, 2026-09-10): the card
 * add-on has a second, independent entry point (the cart page's own "+ Add personalized card"
 * control, snippets/cart-line-card-field.liquid) — a separate component instance with its own
 * private #cardLineKey. A customer can trigger both before either has confirmed with the
 * server, each believing "not in cart yet," producing two lines. See #syncCardWithCart for the
 * fix (re-check a fresh cart fetch before deciding add vs. update) and its dropped-edit fix.
 * @extends Component<ProductPersonalizationRefs>
 */
class ProductPersonalizationComponent extends Component {
  // Not in requiredRefs: the cart-page reuse (snippets/cart-line-card-field.liquid) has no
  // "Estimated total" line at all, since the real cart total is already shown by
  // snippets/cart-summary.liquid — see #updateEstimatedTotal's own guard for the other half
  // of this.

  /** @type {string[]} */
  #selectedColors = [];

  /** @type {string[]} Selected rose colours — one by default, up to `data-max-rose-colors`. */
  #selectedRoseColors = [];

  /** @type {string} */
  #flowerTheme = '';

  /** @type {boolean} */
  #flowerOpen = false;

  /** @type {number} */
  #flowerHighlightIndex = -1;

  /** @type {boolean} */
  #cardAdded = false;

  /** @type {boolean} */
  #cardBusy = false;

  /** @type {number | undefined} */
  #cardUpdateTimeout;

  /** @type {string} Cart line key of this product's own card line, once added — see #findOwnLine. */
  #cardLineKey = '';

  /**
   * @type {boolean} Set when a card sync was requested while one was already in flight, so it
   * would otherwise be silently dropped — see #syncCardWithCart.
   */
  #cardResyncPending = false;

  /** @type {boolean} */
  #ribbonAdded = false;

  /** @type {boolean} */
  #ribbonBusy = false;

  /** @type {number | undefined} */
  #ribbonUpdateTimeout;

  /** @type {string} Cart line key of this product's own ribbon line, once added — see #findOwnLine. */
  #ribbonLineKey = '';

  /** @type {boolean} */
  #liliesAdded = false;

  /** @type {boolean} */
  #liliesBusy = false;

  /** @type {string} Cart line key of this product's own lilies line, once added — see #findOwnLine. */
  #liliesLineKey = '';

  /** @type {boolean} */
  #matchingBouquetAdded = false;

  /** @type {boolean} */
  #matchingBouquetBusy = false;

  /** @type {string} Cart line key of this product's own matching-bouquet line, once added — see #findOwnLine. */
  #matchingBouquetLineKey = '';

  /** @type {boolean} */
  #vaseAdded = false;

  /** @type {boolean} */
  #vaseBusy = false;

  /** @type {string} Cart line key of this product's own vase line, once added — see #findOwnLine. */
  #vaseLineKey = '';

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    // Browsers can restore <select> values on reload/back, so derive the hidden Number
    // property from whatever the dropdowns show rather than assuming they start blank.
    this.#syncNumberProperty();
    this.#updateReadyState();
    this.#updateEstimatedTotal();

    // Cart-page reuse only (see snippets/cart-line-card-field.liquid): the message textarea
    // can already be pre-filled server-side with an existing card's message, for a line item
    // that already has one. Without this, #cardAdded would start false regardless of that
    // text being present, and the first keystroke would try to add a second card line instead
    // of recognizing one already exists.
    if (this.dataset.existingCardLineKey) {
      this.#cardAdded = true;
      this.#cardLineKey = this.dataset.existingCardLineKey;
      this.#updateCardWordCount();
    }

    const { signal } = this.#abortController;
    document.addEventListener('click', this.#handleDocumentClick, { signal });
    // Keeps every add-on in sync if its line is removed/changed from somewhere else
    // (e.g. the cart drawer) rather than through this component's own controls.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleExternalCartUpdate, { signal });
    // Keeps "Estimated total" in sync with the selected variant. Without this, `data-base-price`
    // stays whatever it was rendered with at page load (selected_or_first_available_variant) and
    // the total silently keeps adding add-ons on top of that stale price after a size/variant change.
    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect,
      { signal }
    );
    // Defense-in-depth for the cross-page carryover bug (see class doc comment): bfcache
    // restores this exact DOM + JS instance on back/forward navigation, so re-sync from a
    // fresh cart fetch rather than trusting whatever state was frozen at cache time. Mirrors
    // the same event.persisted guard already used elsewhere in this theme for this class of
    // bug (assets/cart-icon.js, assets/scroll-container.js).
    window.addEventListener('pageshow', this.#handlePageShow, { signal });

    // Required-field gate (see #updateReadyState). Only wires up when this block actually
    // marks something mandatory — a card-only block, or any block with no `require_*` flag,
    // skips all of this and behaves exactly as before.
    if (this.#hasRequiredFields()) {
      // The buy-buttons block may parse AFTER this one (it does on every current product
      // template), so the add-to-cart button isn't in the DOM yet at connect time — re-run
      // once the frame settles so the initial disabled state actually lands on the button.
      requestAnimationFrame(() => this.#updateReadyState());

      // Belt-and-braces alongside the disabled button: product-form-component's own submit
      // handler already bails when the add-to-cart button is disabled (it checks
      // anyButtonDisabled), so the sale can't go through with fields blank. This adds the
      // visible feedback for a keyboard/Enter submit attempt — reveal the checklist and
      // scroll it into view — and preventDefault as extra insurance.
      const form = document.getElementById(this.dataset.productFormId ?? '');
      form?.addEventListener('submit', this.#handleFormSubmitCapture, { capture: true, signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @param {PointerEvent & {target: HTMLElement}} event */
  toggleColor(event) {
    const swatch = /** @type {HTMLButtonElement | null} */ (event.target.closest('[data-color-name]'));
    if (!swatch) return;

    const name = swatch.dataset.colorName ?? '';
    const maxColors = Number(this.dataset.maxColors) || 2;
    const index = this.#selectedColors.indexOf(name);

    if (index >= 0) {
      this.#selectedColors.splice(index, 1);
      this.#setColorStatus('');
    } else if (this.#selectedColors.length >= maxColors) {
      const label = maxColors === 1 ? 'colour' : 'colours';
      this.#setColorStatus(`You can choose up to ${maxColors} ${label}.`, true);
      return;
    } else {
      this.#selectedColors.push(name);
      this.#setColorStatus('');
    }

    this.#syncSwatchState();
    this.#syncColorProperty();
    this.#updateReadyState();
  }

  /**
   * Rose colour is single-select (unlike the bubble colour swatches, which allow up to
   * `max_colors`) — picking a new swatch replaces whichever one was previously selected.
   * @param {PointerEvent & {target: HTMLElement}} event
   */
  selectRoseColor(event) {
    const swatch = /** @type {HTMLButtonElement | null} */ (event.target.closest('[data-rose-color-name]'));
    if (!swatch) return;

    const name = swatch.dataset.roseColorName ?? '';
    const maxRoseColors = Number(this.dataset.maxRoseColors) || 1;
    const index = this.#selectedRoseColors.indexOf(name);

    if (maxRoseColors <= 1) {
      // Single-select: picking a new swatch replaces whichever one was selected.
      this.#selectedRoseColors = [name];
    } else if (index >= 0) {
      this.#selectedRoseColors.splice(index, 1);
    } else if (this.#selectedRoseColors.length >= maxRoseColors) {
      this.#setRoseColorStatus(`You can choose up to ${maxRoseColors} colours.`, true);
      return;
    } else {
      this.#selectedRoseColors.push(name);
    }

    this.#syncRoseSwatchState();
    this.#syncRoseColorProperty();
    this.#setRoseColorStatus('');
    this.#updateReadyState();
  }

  /**
   * @param {string} message - Warning text to show, or '' to fall back to the "Selected: …" summary.
   * @param {boolean} [isWarning]
   */
  #setRoseColorStatus(message, isWarning = false) {
    const { roseColorLabel } = this.refs;
    if (!roseColorLabel) return;

    roseColorLabel.textContent =
      message || (this.#selectedRoseColors.length ? `Selected: ${this.#selectedRoseColors.join(' + ')}` : '');
    roseColorLabel.classList.toggle('product-personalization__status--warning', isWarning);
  }

  handleMessageInput() {
    const { messageInput } = this.refs;
    const maxWords = Number(this.dataset.maxWords) || 10;
    const parts = messageInput.value.trim().split(/\s+/).filter(Boolean);

    if (parts.length > maxWords) {
      messageInput.value = parts.slice(0, maxWords).join(' ');
    }

    this.#updateWordCount();
    this.#syncMessageProperty();
    this.#updateReadyState();
  }

  /** @param {MouseEvent} event */
  toggleFlowerDropdown(event) {
    event.stopPropagation();

    if (this.#flowerOpen) {
      this.#closeFlowerDropdown();
    } else {
      this.#openFlowerDropdown();
    }
  }

  /** @param {KeyboardEvent} event */
  handleFlowerTriggerKeydown(event) {
    const options = this.refs.flowerDropdownOption ?? [];
    if (!options.length) return;

    const { key } = event;

    if (!this.#flowerOpen) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.#openFlowerDropdown();

        const selectedIndex = options.findIndex((option) => option.dataset.value === this.#flowerTheme);
        const startIndex = selectedIndex >= 0 ? selectedIndex : key === 'ArrowUp' ? options.length - 1 : 0;
        this.#highlightFlowerOption(startIndex);
      }
      return;
    }

    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#highlightFlowerOption(Math.min(this.#flowerHighlightIndex + 1, options.length - 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#highlightFlowerOption(Math.max(this.#flowerHighlightIndex - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        this.#highlightFlowerOption(0);
        break;
      case 'End':
        event.preventDefault();
        this.#highlightFlowerOption(options.length - 1);
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.#flowerHighlightIndex >= 0) {
          this.#selectFlowerOption(options[this.#flowerHighlightIndex]);
        }
        this.#closeFlowerDropdown();
        break;
      case 'Escape':
        event.preventDefault();
        this.#closeFlowerDropdown();
        break;
      case 'Tab':
        this.#closeFlowerDropdown();
        break;
    }
  }

  /** @param {MouseEvent & {target: HTMLElement}} event */
  handleFlowerOptionClick(event) {
    const option = /** @type {HTMLLIElement | null} */ (event.target.closest('[data-value]'));
    if (!option) return;

    this.#selectFlowerOption(option);
    this.#closeFlowerDropdown();
  }

  /**
   * Cart-page reuse only (see snippets/cart-line-card-field.liquid) — reveals the card message
   * field in place of the "+ Add personalized card" link. One-way for the rest of this page
   * visit: it does not hide again just because the field is cleared back to empty while the
   * customer is actively editing it (that would yank the field out from under them mid-edit).
   * It only collapses back via #setCardFieldVisible(false), which runs when the card is
   * confirmed removed by something other than this field itself (e.g. the cart drawer's own
   * remove control) or on a fresh page load that never had a card to begin with. On a normal
   * product page this ref doesn't exist, so this method never has anything to do.
   */
  toggleCardReveal() {
    this.#setCardFieldVisible(true);
    this.refs.cardMessageInput?.focus();
  }

  /**
   * @param {boolean} visible
   */
  #setCardFieldVisible(visible) {
    const { cardRevealLink, cardFieldWrapper } = this.refs;
    if (!cardRevealLink || !cardFieldWrapper) return;

    cardFieldWrapper.hidden = !visible;
    cardRevealLink.hidden = visible;
  }

  /**
   * Cart-page reuse only (see snippets/cart-line-card-field.liquid) — shows/hides the
   * "Personalised card added. KSh X." line grouped with this line item's own message field.
   * Called from every place #cardAdded changes so it always reflects the current optimistic
   * state instantly, rather than waiting on the debounced add-to-cart network call to resolve.
   * @param {boolean} added
   */
  #setCardConfirmation(added) {
    const { cardConfirmation } = this.refs;
    if (!cardConfirmation) return;

    cardConfirmation.hidden = !added;
    if (!added) return;

    const price = Number(this.dataset.cardPrice) || 0;
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';
    cardConfirmation.textContent = `Personalised card added. ${formatMoney(price, format, currency)}.`;
  }

  /**
   * Word-caps the card message, updates the live counter, and flips the optimistic "added"
   * state (and therefore "Estimated total") the instant the field goes from empty to non-empty
   * or back — typing the first character is what adds the card (item 2b — there's no separate
   * checkbox); clearing the field back to empty removes it. The actual cart add/remove/update
   * network call is debounced separately (see #syncCardWithCart) so it doesn't fire on every
   * keystroke, but the total itself updates immediately rather than waiting on that debounce.
   */
  handleCardMessageInput() {
    const { cardMessageInput } = this.refs;
    if (!cardMessageInput) return;

    const maxWords = Number(this.dataset.cardMaxWords) || 100;
    const parts = cardMessageInput.value.trim().split(/\s+/).filter(Boolean);

    // Same over-the-limit handling as handleFreeTextInput: ignore the extra keystroke rather
    // than re-joining the words (which glued later letters onto the last word and also
    // flattened line breaks).
    if (parts.length > maxWords) {
      cardMessageInput.value = cardMessageInput.dataset.lastFittingValue ?? parts.slice(0, maxWords).join(' ');
    } else {
      cardMessageInput.dataset.lastFittingValue = cardMessageInput.value;
    }

    this.#updateCardWordCount();

    const hasText = cardMessageInput.value.trim().length > 0;

    if (this.#addonsGoWithMainProduct) {
      // Product page: the card goes into the cart with the main product (collectAddonCartItems).
      if (hasText !== this.#cardAdded) {
        this.#cardAdded = hasText;
        this.#updateEstimatedTotal();
        this.#setCardConfirmation(hasText);
      }
      return;
    }

    if (hasText !== this.#cardAdded) {
      this.#cardAdded = hasText; // optimistic — reverted by #revertCardToggle() if the cart call below fails
      this.#updateEstimatedTotal();
      this.#setCardConfirmation(hasText);
      this.#bumpCartIconOptimistically(hasText ? 1 : -1);
    }

    if (this.#cardUpdateTimeout) clearTimeout(this.#cardUpdateTimeout);
    this.#cardUpdateTimeout = /** @type {any} */ (setTimeout(() => this.#syncCardWithCart(hasText), 600));
  }

  /**
   * Toggles the paid "Add a personalised ribbon" add-on — only rendered when the ribbon
   * isn't free (see the always-on free-ribbon branch handled by handleRibbonMessageInput
   * alone, with no toggle at all). Unlike the card add-on, the ribbon keeps its explicit
   * toggle button (only the card's interaction changed per item 2b).
   */
  async toggleRibbon() {
    const { ribbonToggle, ribbonDetails } = this.refs;
    if (!ribbonToggle || !ribbonDetails || this.#ribbonBusy) return;

    const next = !this.#ribbonAdded;
    this.#ribbonAdded = next; // optimistic — reverted by #revertRibbonToggle() if the cart call fails
    ribbonToggle.setAttribute('aria-pressed', String(next));
    ribbonToggle.setAttribute('aria-expanded', String(next));
    ribbonDetails.hidden = !next;
    this.#setRibbonStatus('');
    this.#updateEstimatedTotal();
    // Product page: added with the main product instead (see collectAddonCartItems).
    if (this.#addonsGoWithMainProduct) return;
    this.#bumpCartIconOptimistically(next ? 1 : -1);

    this.#setRibbonBusy(true);
    if (next) {
      await this.#addRibbonToCart();
    } else {
      await this.#removeRibbonFromCart();
    }
    this.#setRibbonBusy(false);
  }

  /**
   * Toggles the paid "Add lilies" add-on (Classic Bouquet) — the standalone "Lillies" product
   * added/removed as its own cart line, exactly like the paid ribbon toggle but with no
   * message field / details panel. Tracked by `_lilies_product_id` (naming this product) via
   * #findOwnLine, same cross-page-carryover fix as card/ribbon.
   */
  async toggleLilies() {
    const { liliesToggle } = this.refs;
    if (!liliesToggle || this.#liliesBusy) return;

    const next = !this.#liliesAdded;
    this.#liliesAdded = next; // optimistic — reverted by #revertLiliesToggle() if the cart call fails
    liliesToggle.setAttribute('aria-pressed', String(next));
    this.#setLiliesStatus('');
    this.#updateEstimatedTotal();
    // Product page: added with the main product instead (see collectAddonCartItems).
    if (this.#addonsGoWithMainProduct) return;
    this.#bumpCartIconOptimistically(next ? 1 : -1);

    this.#setLiliesBusy(true);
    if (next) {
      await this.#addLiliesToCart();
    } else {
      await this.#removeLiliesFromCart();
    }
    this.#setLiliesBusy(false);
  }

  /**
   * Toggles the paid "Add a matching bouquet (discounted)" add-on (Cake / Garland / Helium
   * Bubble) — a standalone bouquet product added/removed as its own cart line, same mechanism
   * as the ribbon/lilies toggles. No colour or flower choice: Luxe Confetti picks a bouquet
   * that matches the bubble, so there's no selector for it. Tracked by
   * `_matching_bouquet_product_id` (naming this product) via #findOwnLine.
   */
  async toggleMatchingBouquet() {
    const { matchingBouquetToggle } = this.refs;
    if (!matchingBouquetToggle || this.#matchingBouquetBusy) return;

    const next = !this.#matchingBouquetAdded;
    this.#matchingBouquetAdded = next; // optimistic — reverted by #revertMatchingBouquetToggle() if the cart call fails
    matchingBouquetToggle.setAttribute('aria-pressed', String(next));
    this.#setMatchingBouquetStatus('');
    this.#updateEstimatedTotal();
    // Product page: added with the main product instead (see collectAddonCartItems).
    if (this.#addonsGoWithMainProduct) return;
    this.#bumpCartIconOptimistically(next ? 1 : -1);

    this.#setMatchingBouquetBusy(true);
    if (next) {
      await this.#addMatchingBouquetToCart();
    } else {
      await this.#removeMatchingBouquetFromCart();
    }
    this.#setMatchingBouquetBusy(false);
  }

  /**
   * Toggles the optional paid "Add a vase" add-on (Classic Bouquet) — the standalone "Vase"
   * product added/removed as its own cart line, identical mechanism to the "Add lilies"
   * toggle (no message field / details panel). Tracked by `_vase_product_id` (naming this
   * product) via #findOwnLine. Never gates add-to-cart.
   */
  async toggleVase() {
    const { vaseToggle } = this.refs;
    if (!vaseToggle || this.#vaseBusy) return;

    const next = !this.#vaseAdded;
    this.#vaseAdded = next; // optimistic — reverted by #revertVaseToggle() if the cart call fails
    vaseToggle.setAttribute('aria-pressed', String(next));
    this.#setVaseStatus('');
    this.#updateEstimatedTotal();
    // Product page: added with the main product instead (see collectAddonCartItems).
    if (this.#addonsGoWithMainProduct) return;
    this.#bumpCartIconOptimistically(next ? 1 : -1);

    this.#setVaseBusy(true);
    if (next) {
      await this.#addVaseToCart();
    } else {
      await this.#removeVaseFromCart();
    }
    this.#setVaseBusy(false);
  }

  /**
   * Word-caps the ribbon message and keeps the counter in sync. Runs the same whether the
   * ribbon is a paid toggle or an always-visible free field — the debounced cart-property
   * update only fires when #ribbonAdded is true, which never happens for a free ribbon
   * (there's no toggle to set it), so this is safe to share between both modes.
   */
  handleRibbonMessageInput() {
    const { ribbonMessageInput, ribbonWordCount } = this.refs;
    if (!ribbonMessageInput || !ribbonWordCount) return;

    const maxWords = Number(this.dataset.ribbonMaxWords) || 5;
    const parts = ribbonMessageInput.value.trim().split(/\s+/).filter(Boolean);

    if (parts.length > maxWords) {
      ribbonMessageInput.value = parts.slice(0, maxWords).join(' ');
    }

    const words = ribbonMessageInput.value.trim() ? ribbonMessageInput.value.trim().split(/\s+/).length : 0;
    ribbonWordCount.textContent = `${words} / ${maxWords} words`;

    if (this.#ribbonAdded && !this.#addonsGoWithMainProduct) {
      if (this.#ribbonUpdateTimeout) clearTimeout(this.#ribbonUpdateTimeout);
      this.#ribbonUpdateTimeout = /** @type {any} */ (setTimeout(() => this.#updateRibbonProperties(), 600));
    }
  }

  /**
   * Word-caps a free (no-charge, no-standalone-product) personalization text field and keeps
   * its counter in sync. Used by the bouquet pattern's optional free text inputs — "Name on
   * box", "Personalise your gift", "What's your card message?", "What's your ribbon message?"
   * (blocks/bouquet-personalization.liquid, enabled per product via `show_name_field` /
   * `show_personalization_message` / `card_free` / `ribbon_free`). The input itself
   * carries `name="properties[...]"` + `form="..."`, so its value submits natively with the
   * product form — there's no hidden mirror input or cart mutation here, unlike the priced card
   * add-on (handleCardMessageInput).
   * @param {InputEvent & {target: HTMLInputElement | HTMLTextAreaElement}} event
   */
  handleFreeTextInput(event) {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement)) return;

    const maxWords = Number(field.dataset.maxWords) || 0;
    const parts = field.value.trim().split(/\s+/).filter(Boolean);

    // Over the limit: put back the last value that fitted, so the extra keystroke is simply
    // ignored. (Trimming to the first N words instead dropped the trailing space, and every
    // letter typed after that glued onto the last word: "Amara Jane Wanjiru" became
    // "Amara Janeanjiru".) A paste that was never under the limit still trims to N words.
    if (maxWords && parts.length > maxWords) {
      field.value = field.dataset.lastFittingValue ?? parts.slice(0, maxWords).join(' ');
    } else {
      field.dataset.lastFittingValue = field.value;
    }

    const counterId = field.dataset.wordCountFor;
    const counter = counterId ? this.querySelector(`[data-word-count-id="${counterId}"]`) : null;
    if (counter) {
      const words = field.value.trim() ? field.value.trim().split(/\s+/).length : 0;
      const unit = maxWords === 1 ? 'word' : 'words';
      counter.textContent = maxWords ? `${words} / ${maxWords} ${unit}` : `${words} ${unit}`;
      counter.classList.toggle('product-personalization__word-count--valid', words >= 1);
    }

    // No-op unless this block has a require_* flag on (see #updateReadyState) — covers
    // require_personalization_message, require_gift_amount, require_denominations, and the
    // card-only block's preferred colours field, the flags a field sharing this handler can carry.
    this.#updateReadyState();
  }

  #syncSwatchState() {
    for (const swatch of this.refs.colorSwatch ?? []) {
      const selected = this.#selectedColors.includes(swatch.dataset.colorName ?? '');
      swatch.setAttribute('aria-pressed', String(selected));
    }
  }

  #syncRoseSwatchState() {
    for (const swatch of this.refs.roseSwatch ?? []) {
      const selected = this.#selectedRoseColors.includes(swatch.dataset.roseColorName ?? '');
      swatch.setAttribute('aria-pressed', String(selected));
    }
  }

  /**
   * Keeps the hidden `properties[Bubble Colour(s)]` input (see product-personalization.liquid)
   * in sync with the live selection, so it's whatever's actually on screen at the moment the
   * main product form submits — this is what makes the choice show up on the order in Admin.
   */
  #syncColorProperty() {
    const { colorPropertyInput } = this.refs;
    if (colorPropertyInput) colorPropertyInput.value = this.#selectedColors.join(', ');
  }

  /** Keeps the hidden `properties[Bubble Message]` input in sync with the live message text. */
  #syncMessageProperty() {
    const { messagePropertyInput, messageInput } = this.refs;
    if (messagePropertyInput) messagePropertyInput.value = messageInput?.value.trim() ?? '';
  }

  /**
   * Keeps the hidden `properties[Flower Colour Theme]` input in sync — this ref only exists
   * when `show_flower_theme` is on, same as the visible dropdown it mirrors.
   */
  #syncFlowerThemeProperty() {
    const { flowerThemePropertyInput } = this.refs;
    if (flowerThemePropertyInput) flowerThemePropertyInput.value = this.#flowerTheme;
  }

  /**
   * Keeps the hidden `properties[Rose Colour]` input in sync — this ref only exists on the
   * standalone-bouquet pattern (bouquet-personalization.liquid), same as the swatches above.
   */
  #syncRoseColorProperty() {
    const { roseColorPropertyInput } = this.refs;
    if (roseColorPropertyInput) roseColorPropertyInput.value = this.#selectedRoseColors.join(', ');
  }

  /**
   * @param {string} message
   * @param {boolean} [isWarning]
   */
  #setColorStatus(message, isWarning = false) {
    const { colorLabel } = this.refs;
    const text = message || (this.#selectedColors.length ? `Selected: ${this.#selectedColors.join(' + ')}` : '');

    colorLabel.textContent = text;
    colorLabel.classList.toggle('product-personalization__status--warning', isWarning);
  }

  #updateWordCount() {
    const { wordCount } = this.refs;
    if (!wordCount) return;
    const maxWords = Number(this.dataset.maxWords) || 10;
    const words = this.#wordCount();

    wordCount.textContent = `${words} / ${maxWords} words`;
    wordCount.classList.toggle('product-personalization__word-count--valid', words >= 1);
  }

  #wordCount() {
    const value = (this.refs.messageInput?.value ?? '').trim();
    return value ? value.split(/\s+/).length : 0;
  }

  #openFlowerDropdown() {
    const { flowerDropdownTrigger, flowerDropdownList } = this.refs;
    if (!flowerDropdownTrigger || !flowerDropdownList) return;

    this.#flowerOpen = true;
    flowerDropdownList.hidden = false;
    flowerDropdownTrigger.setAttribute('aria-expanded', 'true');
  }

  #closeFlowerDropdown() {
    const { flowerDropdownTrigger, flowerDropdownList } = this.refs;
    if (!flowerDropdownTrigger || !flowerDropdownList) return;

    this.#flowerOpen = false;
    flowerDropdownList.hidden = true;
    flowerDropdownTrigger.setAttribute('aria-expanded', 'false');
    this.#highlightFlowerOption(-1);
  }

  /** @param {number} index */
  #highlightFlowerOption(index) {
    const { flowerDropdownTrigger } = this.refs;
    const options = this.refs.flowerDropdownOption ?? [];

    this.#flowerHighlightIndex = index;

    for (const [i, option] of options.entries()) {
      option.classList.toggle('product-personalization__dropdown-option--highlighted', i === index);
    }

    if (!flowerDropdownTrigger) return;

    const highlighted = options[index];
    if (highlighted) {
      flowerDropdownTrigger.setAttribute('aria-activedescendant', highlighted.id);
      highlighted.scrollIntoView({ block: 'nearest' });
    } else {
      flowerDropdownTrigger.removeAttribute('aria-activedescendant');
    }
  }

  /** @param {HTMLLIElement} option */
  #selectFlowerOption(option) {
    const { flowerDropdownValue } = this.refs;
    const value = option.dataset.value ?? '';

    this.#flowerTheme = value;

    for (const opt of this.refs.flowerDropdownOption ?? []) {
      opt.setAttribute('aria-selected', String(opt === option));
    }

    if (flowerDropdownValue) {
      flowerDropdownValue.textContent = value;
      flowerDropdownValue.classList.remove('product-personalization__dropdown-value--placeholder');
    }

    this.#syncFlowerThemeProperty();
    this.#updateReadyState();
  }

  /** @param {MouseEvent} event */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Node)) return;

    if (this.#flowerOpen && !this.refs.flowerDropdown?.contains(event.target)) {
      this.#closeFlowerDropdown();
    }
  };

  /**
   * Optimistically nudges the header cart bubble before the network call resolves, so every
   * toggle/selection feels instant. cart-icon's own StandardEvents.cartLinesUpdate listener
   * corrects this to the real server count as soon as our dispatched event resolves (or, on
   * failure, the call sites above undo this bump directly).
   * @param {number} delta
   */
  #bumpCartIconOptimistically(delta) {
    if (!delta) return;

    const cartIcon = /** @type {{ currentCartCount?: number, renderCartBubble?: (count: number, animate?: boolean) => void } | null} */ (
      document.querySelector('cart-icon')
    );
    if (!cartIcon || typeof cartIcon.currentCartCount !== 'number' || !cartIcon.renderCartBubble) return;

    cartIcon.renderCartBubble(Math.max(0, cartIcon.currentCartCount + delta));
  }

  /**
   * Section IDs for every `cart-items-component` on the page (cart drawer and/or cart page).
   * Passed as the `sections` request param so add/change responses include pre-rendered HTML —
   * without it, cart-items-component falls back to a slow, uncached full section re-fetch
   * instead of morphing the response HTML in place. That fallback is invisible when the drawer
   * is closed, but very visible (and slow) when it's open.
   */
  #getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter((id) => !!id)
      .join(',');
  }

  /**
   * Finds THIS product's own card/ribbon line among a cart's items — scoped by both variant id
   * and the hidden `_card_product_id`/`_ribbon_product_id` property set on add, not just by
   * variant presence. This scoping is the root-cause fix for the cross-page carryover bug (see
   * class doc comment): without it, any product sharing the same card/ribbon variant would
   * match as soon as ANY product's card/ribbon was in the cart.
   * @param {Array<{variant_id: number|string, quantity: number, properties?: Record<string,string>, key?: string}>} items
   * @param {string} variantId
   * @param {'_card_product_id' | '_ribbon_product_id'} productIdPropertyKey
   * @returns {{key?: string, properties?: Record<string,string>} | undefined}
   */
  #findOwnLine(items, variantId, productIdPropertyKey) {
    const productId = this.dataset.productId ?? '';
    // Add-ons added with their main product carry that line's `_lc_group` (see
    // collectAddonCartItems). A cart-page card control for a grouped line only matches that
    // exact group; for a legacy, ungrouped line it only matches ungrouped add-ons — so two
    // lines of the same product never claim each other's card.
    const group = this.dataset.lcGroup ?? '';
    return items.find(
      (item) =>
        String(item.variant_id) === String(variantId) &&
        item.quantity > 0 &&
        String(item.properties?.[productIdPropertyKey] ?? '') === productId &&
        String(item.properties?._lc_group ?? '') === group
    );
  }

  /**
   * Product pages (this block sits next to a product form) no longer put add-ons in the cart
   * while the shopper is still choosing: the card message, ribbon, lilies, vase and matching
   * bouquet only change local state and "Estimated total", and go into the cart together with
   * the main product in one request (assets/product-form.js → collectAddonCartItems). Adding
   * them on the spot fired a real AddToCart for every visitor who typed a card message or
   * flipped a toggle, even if they never added the product itself (seen in Meta ads data,
   * 2026-10-06). The cart page's own card control (snippets/cart-line-card-field.liquid, no
   * product form) keeps adding directly, since its main product is already in the cart.
   * @returns {boolean}
   */
  get #addonsGoWithMainProduct() {
    return !!this.dataset.productFormId;
  }

  /**
   * The add-on cart lines to add alongside the main product, in the same request, for whatever
   * the shopper has chosen on this product page. Every line (and the main product line, set
   * by product-form.js) carries the same hidden `_lc_group`, which is what ties these add-ons
   * to that exact line in the cart: cart-products.liquid pairs and cascade-removes by it, so
   * the same product added twice with different personalisation keeps its own add-ons.
   * Property names are unchanged from the previous direct-add flow, so orders read the same.
   * @param {string} groupId
   * @returns {{ id: number, quantity: number, properties: Record<string, string> }[]}
   */
  collectAddonCartItems(groupId) {
    if (!this.#addonsGoWithMainProduct) return [];
    const productId = this.dataset.productId ?? '';
    /** @type {{ id: number, quantity: number, properties: Record<string, string> }[]} */
    const items = [];
    /** @param {string | undefined} variantId @param {Record<string, string>} properties */
    const push = (variantId, properties) => {
      if (!variantId) return;
      items.push({ id: Number(variantId), quantity: 1, properties: { ...properties, _lc_group: groupId } });
    };

    const cardMessage = this.refs.cardMessageInput?.value.trim() ?? '';
    if (this.#cardAdded && cardMessage) {
      push(this.dataset.cardVariantId, { 'Card Message': cardMessage, _card_product_id: productId });
    }
    if (this.#ribbonAdded) {
      push(this.dataset.ribbonVariantId, {
        'Ribbon Message': this.refs.ribbonMessageInput?.value.trim() ?? '',
        _ribbon_product_id: productId,
      });
    }
    if (this.#liliesAdded) push(this.dataset.liliesVariantId, { _lilies_product_id: productId });
    if (this.#matchingBouquetAdded) {
      push(this.dataset.matchingBouquetVariantId, { _matching_bouquet_product_id: productId });
    }
    if (this.#vaseAdded) push(this.dataset.vaseVariantId, { _vase_product_id: productId });
    return items;
  }

  /**
   * Generic add/remove-one-variant flow shared by the card and ribbon add-ons: dispatches
   * CartLinesUpdateEvent, performs the fetch, and resolves/rejects the event promise. For
   * 'remove', `lineKey` targets the specific cart line (falling back to the bare variant id if
   * omitted) — required once more than one line can share a variant id, which happens as soon
   * as two different products both add the same shared card/ribbon product (see the
   * `_card_product_id`/`_ribbon_product_id` properties set by the callers below).
   * @param {'add' | 'remove'} action
   * @param {string} variantId
   * @param {Record<string, string>} [properties]
   * @param {string} [lineKey]
   * @returns {Promise<{ok: boolean, key?: string}>}
   */
  async #mutateCartVariant(action, variantId, properties, lineKey) {
    const isAdd = action === 'add';
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'product',
        lines: isAdd
          ? [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }]
          : [{ id: lineKey || variantId, quantity: 0 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const url = isAdd ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
      const body = isAdd
        ? { id: variantId, quantity: 1, properties: properties ?? {}, sections: this.#getCartSectionIds() }
        : { id: lineKey || variantId, quantity: 0, sections: this.#getCartSectionIds() };

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (isAdd && data.status) {
        const error = new Error(data.message || 'Add to cart failed');
        deferredEventPromise.reject(error);
        this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
        return { ok: false };
      }

      if (isAdd) {
        const cart = await this.#fetchCart();
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            items: cart.items,
            source: 'product-personalization',
            itemCount: cart.item_count,
            sections: data.sections,
            didError: false,
          },
        });
        return { ok: true, key: data.key };
      } else {
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
          detail: {
            items: data.items,
            source: 'product-personalization',
            itemCount: data.item_count,
            sections: data.sections,
            didError: false,
          },
        });
        return { ok: true };
      }
    } catch (error) {
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || `Network error trying to ${action} an item`,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      return { ok: false };
    }
  }

  /** @param {CartLinesUpdateEvent} event */
  #handleExternalCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        // Product page add-ons are local choices for the NEXT add, not a mirror of the cart.
        if (!detail?.items || this.#addonsGoWithMainProduct) return;
        this.#syncCardStateFromCartItems(detail.items);
        this.#syncRibbonStateFromCartItems(detail.items);
        this.#syncLiliesStateFromCartItems(detail.items);
        this.#syncMatchingBouquetStateFromCartItems(detail.items);
        this.#syncVaseStateFromCartItems(detail.items);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-personalization] Cart sync failed:', error);
      });
  };

  /**
   * Re-syncs card/ribbon state from a fresh cart fetch when this exact instance is restored
   * from bfcache (see the class doc comment's "defense-in-depth" note).
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    if (!event.persisted || this.#addonsGoWithMainProduct) return;

    this.#fetchCart()
      .then((cart) => {
        this.#syncCardStateFromCartItems(cart.items ?? []);
        this.#syncRibbonStateFromCartItems(cart.items ?? []);
        this.#syncLiliesStateFromCartItems(cart.items ?? []);
        this.#syncMatchingBouquetStateFromCartItems(cart.items ?? []);
        this.#syncVaseStateFromCartItems(cart.items ?? []);
      })
      .catch((error) => console.warn('[product-personalization] bfcache resync failed:', error));
  };

  /**
   * Reconciles the card state with what's actually in the cart FOR THIS PRODUCT specifically —
   * see #findOwnLine and the class doc comment's root-cause note.
   * @param {Array<{variant_id: number|string, quantity: number, properties?: Record<string,string>, key?: string}>} items
   */
  #syncCardStateFromCartItems(items) {
    const variantId = this.dataset.cardVariantId;
    if (!variantId || !this.refs.cardMessageInput) return;
    if (this.#cardBusy) return; // a request is already in flight; let it settle first

    const line = this.#findOwnLine(items, variantId, '_card_product_id');
    const isInCart = !!line;
    this.#cardLineKey = line?.key ?? '';

    if (isInCart === this.#cardAdded) return;

    this.#cardAdded = isInCart;
    this.#setCardFieldVisible(isInCart);
    this.#setCardConfirmation(isInCart);

    if (isInCart && !this.refs.cardMessageInput.value.trim() && line?.properties?.['Card Message']) {
      this.refs.cardMessageInput.value = line.properties['Card Message'];
      this.#updateCardWordCount();
    }

    this.#updateEstimatedTotal();
  }

  /**
   * Same reconciliation as #syncCardStateFromCartItems, for the ribbon toggle. Never runs for
   * a free/always-on ribbon since that mode has no ribbonToggle ref at all.
   * @param {Array<{variant_id: number|string, quantity: number, properties?: Record<string,string>, key?: string}>} items
   */
  #syncRibbonStateFromCartItems(items) {
    const variantId = this.dataset.ribbonVariantId;
    if (!variantId || !this.refs.ribbonToggle) return;
    if (this.#ribbonBusy) return;

    const line = this.#findOwnLine(items, variantId, '_ribbon_product_id');
    const isInCart = !!line;
    this.#ribbonLineKey = line?.key ?? '';

    if (isInCart === this.#ribbonAdded) return;

    const { ribbonToggle, ribbonDetails } = this.refs;
    this.#ribbonAdded = isInCart;
    ribbonToggle.setAttribute('aria-pressed', String(isInCart));
    ribbonToggle.setAttribute('aria-expanded', String(isInCart));
    if (ribbonDetails) ribbonDetails.hidden = !isInCart;
    this.#updateEstimatedTotal();
  }

  /**
   * Same reconciliation as #syncRibbonStateFromCartItems, for the "Add lilies" toggle. No-op
   * on any product without the toggle (no liliesToggle ref).
   * @param {Array<{variant_id: number|string, quantity: number, properties?: Record<string,string>, key?: string}>} items
   */
  #syncLiliesStateFromCartItems(items) {
    const variantId = this.dataset.liliesVariantId;
    if (!variantId || !this.refs.liliesToggle) return;
    if (this.#liliesBusy) return;

    const line = this.#findOwnLine(items, variantId, '_lilies_product_id');
    const isInCart = !!line;
    this.#liliesLineKey = line?.key ?? '';

    if (isInCart === this.#liliesAdded) return;

    this.#liliesAdded = isInCart;
    this.refs.liliesToggle.setAttribute('aria-pressed', String(isInCart));
    this.#updateEstimatedTotal();
  }

  /**
   * Same reconciliation as #syncLiliesStateFromCartItems, for the "Add a matching bouquet"
   * toggle. No-op on any product without the toggle (no matchingBouquetToggle ref).
   * @param {Array<{variant_id: number|string, quantity: number, properties?: Record<string,string>, key?: string}>} items
   */
  #syncMatchingBouquetStateFromCartItems(items) {
    const variantId = this.dataset.matchingBouquetVariantId;
    if (!variantId || !this.refs.matchingBouquetToggle) return;
    if (this.#matchingBouquetBusy) return;

    const line = this.#findOwnLine(items, variantId, '_matching_bouquet_product_id');
    const isInCart = !!line;
    this.#matchingBouquetLineKey = line?.key ?? '';

    if (isInCart === this.#matchingBouquetAdded) return;

    this.#matchingBouquetAdded = isInCart;
    this.refs.matchingBouquetToggle.setAttribute('aria-pressed', String(isInCart));
    this.#updateEstimatedTotal();
  }

  /**
   * Same reconciliation as #syncLiliesStateFromCartItems, for the "Add a vase" toggle. No-op
   * on any product without the toggle (no vaseToggle ref).
   * @param {Array<{variant_id: number|string, quantity: number, properties?: Record<string,string>, key?: string}>} items
   */
  #syncVaseStateFromCartItems(items) {
    const variantId = this.dataset.vaseVariantId;
    if (!variantId || !this.refs.vaseToggle) return;
    if (this.#vaseBusy) return;

    const line = this.#findOwnLine(items, variantId, '_vase_product_id');
    const isInCart = !!line;
    this.#vaseLineKey = line?.key ?? '';

    if (isInCart === this.#vaseAdded) return;

    this.#vaseAdded = isInCart;
    this.refs.vaseToggle.setAttribute('aria-pressed', String(isInCart));
    this.#updateEstimatedTotal();
  }

  async #addCardToCart() {
    const variantId = this.dataset.cardVariantId;
    if (!variantId) {
      console.warn(
        '[product-personalization] Card add-on has no configured variant ID (block setting "card_variant_id" is blank) — nothing was added to cart.'
      );
      return;
    }

    const message = this.refs.cardMessageInput?.value.trim() ?? '';
    const result = await this.#mutateCartVariant('add', variantId, {
      'Card Message': message,
      _card_product_id: this.dataset.productId ?? '',
      ...(this.dataset.lcGroup ? { _lc_group: this.dataset.lcGroup } : {}),
    });

    if (!result.ok) {
      this.#setCardStatus("Couldn't add the card. Please try again.", true);
      this.#bumpCartIconOptimistically(-1); // undo the optimistic bump from handleCardMessageInput()
      this.#revertCardToggle();
    } else {
      this.#cardLineKey = result.key ?? '';
    }
  }

  async #removeCardFromCart() {
    const variantId = this.dataset.cardVariantId;
    if (!variantId) return;

    const result = await this.#mutateCartVariant('remove', variantId, undefined, this.#cardLineKey);

    if (!result.ok) {
      this.#setCardStatus("Couldn't remove the card. Please try again.", true);
      this.#bumpCartIconOptimistically(1); // undo the optimistic bump from handleCardMessageInput()
      // Card is still in the cart server-side; keep the UI reflecting that.
      this.#revertCardToggle(true);
    } else {
      this.#cardLineKey = '';
    }
  }

  /**
   * Adds/removes/updates the card cart line to match whichever state handleCardMessageInput
   * already flipped the UI (and "Estimated total") to — runs 600ms after the customer stops
   * typing, so the network call itself doesn't fire on every keystroke even though the total
   * updates immediately.
   *
   * ROOT CAUSE FIX — real duplicate-charge bug (order #1004, 2026-09-10): this component's
   * #cardLineKey is private, in-memory state, local to ONE instance. The card add-on has a
   * second, independent entry point — the cart page's own "+ Add personalized card" control
   * (snippets/cart-line-card-field.liquid) — which is a *separate* instance with its own
   * #cardLineKey, starting empty. Confirmed sequence on that order: the customer typed a card
   * message on the product page (add debounced, not yet landed), then, seeing no confirmation
   * yet, added a second card via the cart page's control before the first had committed —
   * both instances independently believed "not in cart yet" and both added, producing two
   * separate "Personalized Card" lines with two slightly different messages. Fix: re-check
   * against a fresh /cart.js fetch (real server state) immediately before deciding, instead of
   * trusting only this instance's own #cardLineKey — closes the gap regardless of which
   * surface or instance got there first.
   *
   * ALSO FIXES — dropped edits: previously, a sync request that arrived while one was already
   * in flight was just discarded (`if (this.#cardBusy) return;` with nothing to pick it back
   * up), so an edit made mid-request could silently never reach the cart. Now such a request
   * sets #cardResyncPending and gets re-run — against the *current* field value, not a stale
   * snapshot — as soon as the in-flight one finishes.
   * @param {boolean} hasText
   */
  async #syncCardWithCart(hasText) {
    if (this.#cardBusy) {
      this.#cardResyncPending = true;
      return;
    }
    this.#setCardBusy(true);

    const variantId = this.dataset.cardVariantId;
    if (variantId) {
      try {
        const cart = await this.#fetchCart();
        const line = this.#findOwnLine(cart.items ?? [], variantId, '_card_product_id');
        this.#cardLineKey = line?.key ?? '';
      } catch (error) {
        // Fall through with whatever #cardLineKey already held — better to risk acting on
        // slightly-stale local state than to block the sync entirely over a network hiccup.
        console.warn('[product-personalization] Could not refresh cart state before syncing card:', error);
      }
    }

    const confirmedInCart = !!this.#cardLineKey;

    if (hasText && !confirmedInCart) {
      await this.#addCardToCart();
    } else if (!hasText && confirmedInCart) {
      await this.#removeCardFromCart();
    } else if (hasText && confirmedInCart) {
      await this.#updateCardProperties();
    }

    this.#setCardBusy(false);

    if (this.#cardResyncPending) {
      this.#cardResyncPending = false;
      const stillHasText = (this.refs.cardMessageInput?.value.trim().length ?? 0) > 0;
      await this.#syncCardWithCart(stillHasText);
    }
  }

  async #updateCardProperties() {
    const variantId = this.dataset.cardVariantId;
    if (!variantId || !this.#cardLineKey) return;

    const message = this.refs.cardMessageInput?.value.trim() ?? '';

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: this.#cardLineKey || variantId,
          quantity: 1,
          properties: {
            'Card Message': message,
            _card_product_id: this.dataset.productId ?? '',
            ...(this.dataset.lcGroup ? { _lc_group: this.dataset.lcGroup } : {}),
          },
        }),
      });
      const cart = await response.json();
      // /cart/change.js's response line key changes whenever properties change (key is derived
      // from variant + properties) — re-resolve it so the next remove/update targets correctly.
      const line = this.#findOwnLine(cart.items ?? [], variantId, '_card_product_id');
      if (line?.key) this.#cardLineKey = line.key;
    } catch (error) {
      console.warn('[product-personalization] Failed to update card message on cart line:', error);
    }
  }

  /** @param {boolean} [added] - Force the card state back to added (true) or not-added (false) after a failed request. */
  #revertCardToggle(added = false) {
    this.#cardAdded = added;
    this.#updateEstimatedTotal();
    this.#setCardConfirmation(added);
  }

  #updateCardWordCount() {
    const { cardMessageInput, cardWordCount } = this.refs;
    if (!cardMessageInput || !cardWordCount) return;
    const maxWords = Number(this.dataset.cardMaxWords) || 100;
    const words = cardMessageInput.value.trim() ? cardMessageInput.value.trim().split(/\s+/).length : 0;
    cardWordCount.textContent = `${words} / ${maxWords} words`;
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setCardStatus(message, isError = false) {
    const { cardStatus } = this.refs;
    if (!cardStatus) return;

    cardStatus.textContent = message;
    cardStatus.classList.toggle('product-personalization__status--warning', isError);
  }

  /** @param {boolean} busy */
  #setCardBusy(busy) {
    this.#cardBusy = busy;
  }

  async #addRibbonToCart() {
    const variantId = this.dataset.ribbonVariantId;
    if (!variantId) {
      console.warn(
        '[product-personalization] Ribbon add-on has no configured variant ID (block setting "ribbon_variant_id" is blank) — nothing was added to cart.'
      );
      return;
    }

    const message = this.refs.ribbonMessageInput?.value.trim() ?? '';
    const result = await this.#mutateCartVariant('add', variantId, {
      'Ribbon Message': message,
      _ribbon_product_id: this.dataset.productId ?? '',
    });

    if (!result.ok) {
      this.#setRibbonStatus("Couldn't add the ribbon. Please try again.", true);
      this.#bumpCartIconOptimistically(-1); // undo the optimistic bump from toggleRibbon()
      this.#revertRibbonToggle();
    } else {
      this.#ribbonLineKey = result.key ?? '';
    }
  }

  async #removeRibbonFromCart() {
    const variantId = this.dataset.ribbonVariantId;
    if (!variantId) return;

    const result = await this.#mutateCartVariant('remove', variantId, undefined, this.#ribbonLineKey);

    if (!result.ok) {
      this.#setRibbonStatus("Couldn't remove the ribbon. Please try again.", true);
      this.#bumpCartIconOptimistically(1); // undo the optimistic bump from toggleRibbon()
      // Ribbon is still in the cart server-side; keep the UI reflecting that.
      this.#revertRibbonToggle(true);
    } else {
      this.#ribbonLineKey = '';
    }
  }

  async #updateRibbonProperties() {
    const variantId = this.dataset.ribbonVariantId;
    if (!variantId || !this.#ribbonAdded) return;

    const message = this.refs.ribbonMessageInput?.value.trim() ?? '';

    try {
      const response = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: this.#ribbonLineKey || variantId,
          quantity: 1,
          properties: { 'Ribbon Message': message, _ribbon_product_id: this.dataset.productId ?? '' },
        }),
      });
      const cart = await response.json();
      const line = this.#findOwnLine(cart.items ?? [], variantId, '_ribbon_product_id');
      if (line?.key) this.#ribbonLineKey = line.key;
    } catch (error) {
      console.warn('[product-personalization] Failed to update ribbon message on cart line:', error);
    }
  }

  /** @param {boolean} [added] - Force the toggle back to added (true) or not-added (false) state. */
  #revertRibbonToggle(added = false) {
    const { ribbonToggle, ribbonDetails } = this.refs;
    this.#ribbonAdded = added;
    this.#updateEstimatedTotal();
    if (!ribbonToggle || !ribbonDetails) return;

    ribbonToggle.setAttribute('aria-pressed', String(added));
    ribbonToggle.setAttribute('aria-expanded', String(added));
    ribbonDetails.hidden = !added;
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setRibbonStatus(message, isError = false) {
    const { ribbonStatus } = this.refs;
    if (!ribbonStatus) return;

    ribbonStatus.textContent = message;
    ribbonStatus.classList.toggle('product-personalization__status--warning', isError);
  }

  /** @param {boolean} busy */
  #setRibbonBusy(busy) {
    this.#ribbonBusy = busy;
    if (this.refs.ribbonToggle) this.refs.ribbonToggle.disabled = busy;
  }

  async #addLiliesToCart() {
    const variantId = this.dataset.liliesVariantId;
    if (!variantId) {
      console.warn(
        '[product-personalization] Lilies add-on has no configured variant ID (block setting "lilies_variant_id" is blank) — nothing was added to cart.'
      );
      this.#revertLiliesToggle();
      return;
    }

    const result = await this.#mutateCartVariant('add', variantId, {
      _lilies_product_id: this.dataset.productId ?? '',
    });

    if (!result.ok) {
      this.#setLiliesStatus("Couldn't add the lilies. Please try again.", true);
      this.#bumpCartIconOptimistically(-1); // undo the optimistic bump from toggleLilies()
      this.#revertLiliesToggle();
    } else {
      this.#liliesLineKey = result.key ?? '';
    }
  }

  async #removeLiliesFromCart() {
    const variantId = this.dataset.liliesVariantId;
    if (!variantId) return;

    const result = await this.#mutateCartVariant('remove', variantId, undefined, this.#liliesLineKey);

    if (!result.ok) {
      this.#setLiliesStatus("Couldn't remove the lilies. Please try again.", true);
      this.#bumpCartIconOptimistically(1); // undo the optimistic bump from toggleLilies()
      this.#revertLiliesToggle(true);
    } else {
      this.#liliesLineKey = '';
    }
  }

  /** @param {boolean} [added] - Force the toggle back to added (true) or not-added (false) state. */
  #revertLiliesToggle(added = false) {
    const { liliesToggle } = this.refs;
    this.#liliesAdded = added;
    this.#updateEstimatedTotal();
    if (liliesToggle) liliesToggle.setAttribute('aria-pressed', String(added));
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setLiliesStatus(message, isError = false) {
    const { liliesStatus } = this.refs;
    if (!liliesStatus) return;

    liliesStatus.textContent = message;
    liliesStatus.classList.toggle('product-personalization__status--warning', isError);
  }

  /** @param {boolean} busy */
  #setLiliesBusy(busy) {
    this.#liliesBusy = busy;
    if (this.refs.liliesToggle) this.refs.liliesToggle.disabled = busy;
  }

  async #addVaseToCart() {
    const variantId = this.dataset.vaseVariantId;
    if (!variantId) {
      console.warn(
        '[product-personalization] Vase add-on has no configured variant ID (block setting "vase_variant_id" is blank) — nothing was added to cart.'
      );
      this.#revertVaseToggle();
      return;
    }

    const result = await this.#mutateCartVariant('add', variantId, {
      _vase_product_id: this.dataset.productId ?? '',
    });

    if (!result.ok) {
      this.#setVaseStatus("Couldn't add the vase. Please try again.", true);
      this.#bumpCartIconOptimistically(-1); // undo the optimistic bump from toggleVase()
      this.#revertVaseToggle();
    } else {
      this.#vaseLineKey = result.key ?? '';
    }
  }

  async #removeVaseFromCart() {
    const variantId = this.dataset.vaseVariantId;
    if (!variantId) return;

    const result = await this.#mutateCartVariant('remove', variantId, undefined, this.#vaseLineKey);

    if (!result.ok) {
      this.#setVaseStatus("Couldn't remove the vase. Please try again.", true);
      this.#bumpCartIconOptimistically(1); // undo the optimistic bump from toggleVase()
      this.#revertVaseToggle(true);
    } else {
      this.#vaseLineKey = '';
    }
  }

  /** @param {boolean} [added] - Force the toggle back to added (true) or not-added (false) state. */
  #revertVaseToggle(added = false) {
    const { vaseToggle } = this.refs;
    this.#vaseAdded = added;
    this.#updateEstimatedTotal();
    if (vaseToggle) vaseToggle.setAttribute('aria-pressed', String(added));
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setVaseStatus(message, isError = false) {
    const { vaseStatus } = this.refs;
    if (!vaseStatus) return;

    vaseStatus.textContent = message;
    vaseStatus.classList.toggle('product-personalization__status--warning', isError);
  }

  /** @param {boolean} busy */
  #setVaseBusy(busy) {
    this.#vaseBusy = busy;
    if (this.refs.vaseToggle) this.refs.vaseToggle.disabled = busy;
  }

  async #addMatchingBouquetToCart() {
    const variantId = this.dataset.matchingBouquetVariantId;
    if (!variantId) {
      console.warn(
        '[product-personalization] Matching-bouquet add-on has no configured variant ID (block setting "matching_bouquet_variant_id" is blank) — nothing was added to cart.'
      );
      this.#revertMatchingBouquetToggle();
      return;
    }

    const result = await this.#mutateCartVariant('add', variantId, {
      _matching_bouquet_product_id: this.dataset.productId ?? '',
    });

    if (!result.ok) {
      this.#setMatchingBouquetStatus("Couldn't add the bouquet. Please try again.", true);
      this.#bumpCartIconOptimistically(-1); // undo the optimistic bump from toggleMatchingBouquet()
      this.#revertMatchingBouquetToggle();
    } else {
      this.#matchingBouquetLineKey = result.key ?? '';
    }
  }

  async #removeMatchingBouquetFromCart() {
    const variantId = this.dataset.matchingBouquetVariantId;
    if (!variantId) return;

    const result = await this.#mutateCartVariant('remove', variantId, undefined, this.#matchingBouquetLineKey);

    if (!result.ok) {
      this.#setMatchingBouquetStatus("Couldn't remove the bouquet. Please try again.", true);
      this.#bumpCartIconOptimistically(1); // undo the optimistic bump from toggleMatchingBouquet()
      this.#revertMatchingBouquetToggle(true);
    } else {
      this.#matchingBouquetLineKey = '';
    }
  }

  /** @param {boolean} [added] - Force the toggle back to added (true) or not-added (false) state. */
  #revertMatchingBouquetToggle(added = false) {
    const { matchingBouquetToggle } = this.refs;
    this.#matchingBouquetAdded = added;
    this.#updateEstimatedTotal();
    if (matchingBouquetToggle) matchingBouquetToggle.setAttribute('aria-pressed', String(added));
  }

  /**
   * @param {string} message
   * @param {boolean} [isError]
   */
  #setMatchingBouquetStatus(message, isError = false) {
    const { matchingBouquetStatus } = this.refs;
    if (!matchingBouquetStatus) return;

    matchingBouquetStatus.textContent = message;
    matchingBouquetStatus.classList.toggle('product-personalization__status--warning', isError);
  }

  /** @param {boolean} busy */
  #setMatchingBouquetBusy(busy) {
    this.#matchingBouquetBusy = busy;
    if (this.refs.matchingBouquetToggle) this.refs.matchingBouquetToggle.disabled = busy;
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Sums the price (in minor units) of the add-ons that stay on the product page: the card
   * (if a message is entered), the ribbon (if selected/entered), lilies, the vase, and the
   * matching bouquet (if toggled on). Every other add-on now lives on the cart page (see
   * assets/cart-addons.js) and no longer contributes here.
   */
  #getAddonsTotal() {
    let total = 0;
    if (this.#cardAdded) total += Number(this.dataset.cardPrice) || 0;
    if (this.#ribbonAdded) total += Number(this.dataset.ribbonPrice) || 0;
    if (this.#liliesAdded) total += Number(this.dataset.liliesPrice) || 0;
    if (this.#matchingBouquetAdded) total += Number(this.dataset.matchingBouquetPrice) || 0;
    if (this.#vaseAdded) total += Number(this.dataset.vasePrice) || 0;
    return total;
  }

  #updateEstimatedTotal() {
    const { estimatedTotal } = this.refs;
    if (!estimatedTotal) return; // cart-page reuse has no total line — see the class field comment above

    const basePrice = Number(this.dataset.basePrice) || 0;
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currency || 'USD';

    estimatedTotal.textContent = formatMoney(basePrice + this.#getAddonsTotal(), format, currency);
  }

  /**
   * Updates `data-base-price` from the newly-selected variant and recomputes "Estimated total"
   * so it always reflects variant price + active add-ons, not just whatever variant was
   * selected/first-available at page load.
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.resource || typeof detail.resource.price !== 'number') return;

        if (detail.newProduct) {
          this.dataset.productId = detail.newProduct.id;
        } else if (detail.productId && this.dataset.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        this.dataset.basePrice = String(detail.resource.price);
        this.#updateEstimatedTotal();
        this.#updateFlowerMatchingNote(detail.resource);
        this.#syncNumberMode(detail.resource);
        // A variant change morphs the buy-buttons block back to its server markup, which
        // re-enables the add-to-cart button — re-apply the required-field gate on top, both
        // now and after the frame settles (the morph may land after this callback).
        this.#updateReadyState();
        requestAnimationFrame(() => this.#updateReadyState());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-personalization] Event promise rejected:', error);
      });
  };

  /**
   * Shows/hides the flower-matching note (see blocks/product-personalization.liquid,
   * `show_flower_matching_note`) to match the newly-selected variant, so it only appears while
   * the customer has the configured option value selected (e.g. Teddy Bubble's "Teddy bear &
   * Flowers", hidden for "Teddy only"). `data-flower-matching-note-variant-value` blank means
   * "always show" — matches the initial server-rendered state computed in the block's Liquid.
   * @param {{ options?: string[] }} resource - The newly-selected variant, as parsed from the
   *   variant-picker's JSON script tag (see ProductSelectEvent's `detail.resource`).
   */
  #updateFlowerMatchingNote(resource) {
    const options = resource.options ?? [];

    // Per-variant wording (`flower_matching_note_variant_texts`): one note per option value,
    // only the one matching the selected variant is shown.
    for (const note of this.querySelectorAll('[data-flower-matching-note-value]')) {
      note.hidden = !options.includes(note.dataset.flowerMatchingNoteValue);
    }

    const { flowerMatchingNote } = this.refs;
    if (!flowerMatchingNote) return;

    const triggerValue = this.dataset.flowerMatchingNoteVariantValue;
    const visible = !triggerValue || options.includes(triggerValue);

    flowerMatchingNote.hidden = !visible;
  }

  /**
   * The "Flower mix" selector is a native radiogroup (standalone-bouquet pattern), so it has
   * no JS-held state of its own — this just re-evaluates the required-field gate when the
   * customer picks an option. Wired via `on:change` on the radiogroup wrapper.
   */
  handleFlowerMixChange() {
    this.#updateReadyState();
  }

  /** Any of the number dropdowns changed (card-only block, `show_number_picker`). */
  handleNumberChange() {
    this.#syncNumberProperty();
    this.#updateReadyState();
  }

  /**
   * True while the two-digit dropdowns are the ones showing.
   * @returns {boolean}
   */
  #isDoubleNumber() {
    return Boolean(this.refs.numberDoubleGroup && !this.refs.numberDoubleGroup.hidden);
  }

  /**
   * Swaps between the single and double digit dropdowns to match the newly-selected variant,
   * and clears the dropdowns being hidden so a stale choice can never be submitted.
   * @param {{ options?: string[] }} resource - The newly-selected variant.
   */
  #syncNumberMode(resource) {
    const { numberSingleGroup, numberDoubleGroup, numberSingleSelect, numberFirstSelect, numberSecondSelect } =
      this.refs;
    if (!numberSingleGroup || !numberDoubleGroup) return;

    const doubleValue = this.dataset.numberDoubleValue || 'Double digit';
    const isDouble = (resource.options ?? []).includes(doubleValue);

    numberSingleGroup.hidden = isDouble;
    numberDoubleGroup.hidden = !isDouble;

    const hiddenSelects = isDouble ? [numberSingleSelect] : [numberFirstSelect, numberSecondSelect];
    for (const select of hiddenSelects) {
      if (select) select.value = '';
    }

    this.#syncNumberProperty();
  }

  /**
   * Writes the chosen number into the hidden `properties[Number]` input: the single digit, or
   * both digits joined ("25") once both are picked. Disabled while empty so a blank "Number"
   * never reaches the cart.
   */
  #syncNumberProperty() {
    const { numberPropertyInput, numberSingleSelect, numberFirstSelect, numberSecondSelect } = this.refs;
    if (!numberPropertyInput) return;

    let value = '';
    if (this.#isDoubleNumber()) {
      const first = numberFirstSelect?.value ?? '';
      const second = numberSecondSelect?.value ?? '';
      if (first && second) value = `${first}${second}`;
    } else {
      value = numberSingleSelect?.value ?? '';
    }

    numberPropertyInput.value = value;
    numberPropertyInput.disabled = !value;
  }

  /**
   * True when this block marks at least one personalization field mandatory (any `require_*`
   * setting on, rendered here as `data-require-*="true"`). When false, the whole required-field
   * gate below is inert and the form behaves exactly as an un-gated product page — that's the
   * card-only-personalization block, and any bubble/bouquet block with every `require_*` off.
   * @returns {boolean}
   */
  #hasRequiredFields() {
    const d = this.dataset;
    return (
      d.requireMessage === 'true' ||
      d.requireColors === 'true' ||
      d.requireFlowerTheme === 'true' ||
      d.requireRoseColor === 'true' ||
      d.requireFlowerMix === 'true' ||
      d.requirePersonalizationMessage === 'true' ||
      d.requireGiftAmount === 'true' ||
      d.requireDenominations === 'true' ||
      d.requireNumber === 'true' ||
      d.requirePreferredColours === 'true'
    );
  }

  /**
   * The mandatory personalization fields that are present on this page AND still unfilled,
   * as human-readable labels for the checklist. A `data-require-*` flag is only honoured when
   * the field it names is actually rendered (its ref/markup exists), so a stray flag on a
   * block that doesn't show that field can never permanently disable add-to-cart.
   * @returns {string[]}
   */
  #missingRequiredFields() {
    return this.#missingRequiredEntries().map((entry) => entry.label);
  }

  /**
   * Same checks as #missingRequiredFields, in the same order, but each entry also carries the
   * element to bring into view (see revealFirstMissingField).
   * @returns {{ label: string, element: Element | null | undefined }[]}
   */
  #missingRequiredEntries() {
    const d = this.dataset;
    /** @type {{ label: string, element: Element | null | undefined }[]} */
    const missing = [];

    if (d.requireMessage === 'true' && this.refs.messageInput && this.#wordCount() < 1) {
      missing.push({ label: d.messageLabel || 'Your message', element: this.refs.messageInput });
    }
    if (d.requireColors === 'true' && this.refs.colorSwatch?.length && this.#selectedColors.length < 1) {
      missing.push({ label: 'Bubble colour', element: this.refs.colorSwatch[0]?.parentElement });
    }
    if (d.requireFlowerTheme === 'true' && this.refs.flowerDropdownTrigger && this.#flowerTheme === '') {
      missing.push({ label: 'Flower colour theme', element: this.refs.flowerDropdownTrigger });
    }
    if (d.requireRoseColor === 'true' && this.refs.roseSwatch?.length && this.#selectedRoseColors.length < 1) {
      missing.push({ label: d.roseColorLabel || 'Flower colour', element: this.refs.roseSwatch[0]?.parentElement });
    }
    if (d.requireFlowerMix === 'true') {
      const group = this.querySelector('input[name="properties[Flower mix]"]');
      if (group && !this.querySelector('input[name="properties[Flower mix]"]:checked')) {
        missing.push({ label: d.flowerMixLabel || 'Flower mix', element: group.closest('fieldset') || group.parentElement });
      }
    }
    if (
      d.requirePersonalizationMessage === 'true' &&
      this.refs.personalizationMessageInput &&
      !this.refs.personalizationMessageInput.value.trim()
    ) {
      missing.push({ label: d.personalizationMessageLabel || 'Your message', element: this.refs.personalizationMessageInput });
    }
    if (
      d.requireGiftAmount === 'true' &&
      this.refs.giftAmountInput &&
      !(Number(this.refs.giftAmountInput.value) > 0)
    ) {
      missing.push({ label: d.giftAmountLabel || 'Gift amount', element: this.refs.giftAmountInput });
    }
    if (
      d.requireDenominations === 'true' &&
      this.refs.denominationsInput &&
      !this.refs.denominationsInput.value.trim()
    ) {
      missing.push({ label: d.denominationsLabel || 'Denominations', element: this.refs.denominationsInput });
    }

    if (d.requireNumber === 'true' && this.refs.numberPropertyInput && !this.refs.numberPropertyInput.value) {
      missing.push({ label: this.#isDoubleNumber() ? 'Both digits' : 'Your number', element: this.refs.numberPropertyInput });
    }
    if (
      d.requirePreferredColours === 'true' &&
      this.refs.preferredColoursInput &&
      !this.refs.preferredColoursInput.value.trim()
    ) {
      missing.push({ label: 'Preferred colour(s)', element: this.refs.preferredColoursInput });
    }

    return missing;
  }

  /**
   * The required-field gate. Only the fields listed in the class doc comment can ever be
   * mandatory, and only when their block opts in via a `require_*` setting; the card / ribbon /
   * lilies / vase / matching-bouquet / free-text add-ons are never part of this. While anything
   * mandatory is unfilled: the product form's add-to-cart button is disabled and a visible
   * checklist of what's missing is shown just above it (blocks/{product,bouquet}-personalization
   * render the `validationSummary` element for this). No-op for blocks with no `require_*` set.
   *
   * The button is only ever re-enabled by us if we were the one who disabled it
   * (`data-personalization-blocked`), so a genuinely unavailable/sold-out product — where the
   * server already rendered the button disabled — is never wrongly re-enabled.
   */
  #updateReadyState() {
    if (!this.#hasRequiredFields()) return;

    const missing = this.#missingRequiredFields();
    const blocked = missing.length > 0;

    const form = document.getElementById(this.dataset.productFormId ?? '');
    const buttons = form?.querySelectorAll('add-to-cart-component button[ref="addToCartButton"]') ?? [];
    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      if (blocked) {
        button.disabled = true;
        button.dataset.personalizationBlocked = 'true';
      } else if (button.dataset.personalizationBlocked === 'true') {
        button.disabled = false;
        delete button.dataset.personalizationBlocked;
      }
    }

    this.#renderValidationSummary(missing);
  }

  /** @param {string[]} missing */
  #renderValidationSummary(missing) {
    const { validationSummary } = this.refs;
    if (!validationSummary) return;

    if (!missing.length) {
      validationSummary.hidden = true;
      validationSummary.textContent = '';
      return;
    }

    validationSummary.textContent = `Still needed before you can add to cart: ${this.#formatList(missing)}.`;
    validationSummary.hidden = false;
  }

  /**
   * "A" / "A and B" / "A, B and C" — for the missing-fields checklist sentence.
   * @param {string[]} items
   * @returns {string}
   */
  #formatList(items) {
    if (items.length <= 1) return items.join('');
    if (items.length === 2) return items.join(' and ');
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
  }

  /**
   * Used by the sticky add-to-cart bar (assets/sticky-add-to-cart.js): when a required field is
   * still empty, refresh the "Still needed" checklist and bring the first missing field into
   * view instead of adding to cart. Doesn't focus text inputs, so the phone keyboard only opens
   * when the shopper taps the field themselves.
   * @returns {boolean} true if something was missing (and the caller should not add to cart)
   */
  revealFirstMissingField() {
    if (!this.#hasRequiredFields()) return false;
    const missing = this.#missingRequiredEntries();
    if (!missing.length) return false;

    this.#updateReadyState();
    const target = missing.find((entry) => entry.element)?.element ?? this.refs.validationSummary;
    if (!target) return true;

    // Prefer a position that shows the missing field AND the "Still needed" checklist (which
    // sits further down, just above Add to cart): field near the top, under the sticky header,
    // checklist above the sticky bar. If they can't both fit, just centre the field.
    const summary = this.refs.validationSummary;
    const header = document.querySelector('#header-component, header-component, .header-section');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    const fieldTop = target.getBoundingClientRect().top + window.scrollY;
    const summaryBottom = summary && !summary.hidden ? summary.getBoundingClientRect().bottom + window.scrollY : fieldTop;
    const usable = window.innerHeight - headerBottom - 110; // leave room for the sticky bar
    if (summaryBottom - fieldTop <= usable) {
      window.scrollTo({ top: Math.max(0, fieldTop - headerBottom - 16), behavior: 'smooth' });
    } else {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
    return true;
  }

  /**
   * Submit-attempt feedback on the product form (only attached when #hasRequiredFields()).
   * The disabled button + product-form-component's own anyButtonDisabled check already stop
   * the sale; this makes a keyboard/Enter submit attempt visible by refreshing the checklist
   * and scrolling it into view, and preventDefault is extra insurance.
   * @param {Event} event
   */
  #handleFormSubmitCapture = (event) => {
    if (!this.#missingRequiredFields().length) return;

    event.preventDefault();
    this.#updateReadyState();
    this.refs.validationSummary?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  };
}

if (!customElements.get('product-personalization-component')) {
  customElements.define('product-personalization-component', ProductPersonalizationComponent);
}
